import HomepageCarouselGrid from "hec/components/homepage-carousel-grid";
import type { HomepageCarouselGridProps } from "hec/components/homepage-carousel-grid/types";
import {
  slidesVisibleValuesDesktop,
  slidesVisibleValuesMobile,
} from "hec/components/ui/carousel.utils";

import type { RegisteredComponent } from "@builder.io/sdk-react";

// Right-panel inputs are owned by the standalone ImageTextCarouselGrid block
// (single source of truth — SFCC-11246). Import instead of re-declaring so the
// homepage composite and the standalone block never drift.
import {
  iconsHeaderTextInput,
  imageTextItemsInput,
} from "../image-text-carousel-grid/inputs";

const HomepageCarouselGridBuilder = (
  props: HomepageCarouselGridProps & { attributes?: Record<string, unknown> },
) => {
  return <HomepageCarouselGrid {...props} />;
};

export const HomepageCarouselGridDefinition = {
  component: HomepageCarouselGridBuilder,
  name: "homepageCarouselGrid",
  noWrap: true,
  inputs: [
    {
      name: "enableHeroOverlap",
      type: "boolean",
      defaultValue: false,
      friendlyName: "Enable Hero Overlap",
      helperText:
        "Enable to allow the carousel grid to overlap with the hero section using negative margin.",
    },
    {
      name: "productCategory",
      type: "SFCommerceCategory",
    },
    {
      name: "enableBasicView",
      type: "boolean",
      defaultValue: true,
      friendlyName: "Enable Basic View",
      helperText:
        "Enable to use the basic view for product tiles in the carousel.",
    },
    {
      name: "productCarouselHeaderText",
      type: "string",
      defaultValue: "Explore More",
      helperText: "The header text for the product carousel.",
    },
    {
      name: "resultCount",
      type: "number",
      defaultValue: 8,
    },
    {
      name: "addedToCartPodId",
      type: "string",
    },
    { name: "productTileBorderColor", type: "color" },
    {
      name: "showNavigationArrows",
      type: "object",
      friendlyName: "Show Navigation Arrows",
      subFields: [
        {
          name: "onDesktop",
          type: "boolean",
          defaultValue: false,
          helperText: "Show navigation arrows on desktop",
        },
        {
          name: "onMobile",
          type: "boolean",
          defaultValue: true,
          helperText: "Show navigation arrows on mobile devices",
        },
      ],
    },
    {
      name: "slidesVisible",
      friendlyName: "Number of Slides Visible",
      type: "object",
      subFields: [
        {
          name: "onDesktop",
          type: "string",
          enum: slidesVisibleValuesDesktop,
          defaultValue: "1",
        },
        {
          name: "onMobile",
          type: "string",
          enum: slidesVisibleValuesMobile,
          defaultValue: "2",
        },
      ],
    },
    {
      name: "showPeek",
      type: "object",
      friendlyName: "Show Peek Options",
      subFields: [
        {
          name: "onDesktop",
          type: "boolean",
          defaultValue: true,
          helperText: "Select true to hide peek on desktop",
        },
        {
          name: "onMobile",
          type: "boolean",
          defaultValue: false,
          helperText: "Select true to hide peek on mobile devices",
        },
      ],
    },
    {
      name: "ButtonNavigationColor",
      type: "color",
      friendlyName: "Button Navigation Color",
      helperText: "Color of the button navigation",
    },
    {
      name: "slidePadding",
      friendlyName: "Padding Between Slides",
      type: "object",
      subFields: [
        {
          name: "onDesktop",
          type: "string",
          enum: Array.from({ length: 7 }, (_v, i) => i.toString()),
          defaultValue: "2",
          helperText: "Padding between slides",
        },
        {
          name: "onMobile",
          friendlyName: "Padding Between Slides Mobile",
          type: "string",
          enum: Array.from({ length: 7 }, (_v, i) => i.toString()),
          defaultValue: "2",
          helperText: "Padding between slides on mobile",
        },
      ],
    },
    iconsHeaderTextInput,
    imageTextItemsInput,
    {
      name: "carouselCtaText",
      type: "string",
      defaultValue: "Shop Now",
      helperText: "The call to action text for the carousel grid.",
    },
    {
      name: "buttonColor",
      type: "color",
      helperText: "The button color for the call to action button.",
    },
    {
      name: "buttonTextColor",
      type: "color",
      helperText: "The text color for the call to action button.",
    },
    {
      name: "external",
      type: "boolean",
      defaultValue: false,
      helperText: "Set to true if the link is external.",
    },
    {
      name: "link",
      type: "reference",
      model: "page",
      showIf: `options.get('external') === false`,
      helperText: "The internal link for the call to action button.",
    },
    {
      name: "external_link",
      type: "reference",
      model: "external-links",
      showIf: `options.get('external') === true`,
      helperText: "The external link for the call to action button.",
    },
  ],
} satisfies RegisteredComponent;
